import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Zap } from "lucide-react";
import { rhm, apiError, dt, titleCase } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Btn, Card, Empty, Field, Input, Modal, PageHead, Select, Table, Td, Textarea } from "../ui";

const EMPTY_RULE = { name: "", trigger: "reorder_due", action: "send_email", channel: "email", audience: "customer", template_id: "", offset_days: 0, is_active: true, description: "" };
const EMPTY_TPL = { name: "", channel: "email", audience: "customer", subject: "", body: "", is_active: true };

export default function RhmAutomation() {
    const { can } = useRhmAuth();
    const qc = useQueryClient();
    const [rule, setRule] = useState(null);
    const [tpl, setTpl] = useState(null);

    const { data: meta } = useQuery({ queryKey: ["rhm-automation-meta"], queryFn: async () => (await rhm.get("/automation/meta")).data });
    const { data: rules = [] } = useQuery({ queryKey: ["rhm-rules"], queryFn: async () => (await rhm.get("/automation/rules")).data });
    const { data: templates = [] } = useQuery({ queryKey: ["rhm-templates"], queryFn: async () => (await rhm.get("/automation/templates")).data });
    const { data: comms } = useQuery({ queryKey: ["rhm-comms"], queryFn: async () => (await rhm.get("/communications")).data });

    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ["rhm-rules"] });
        qc.invalidateQueries({ queryKey: ["rhm-templates"] });
        qc.invalidateQueries({ queryKey: ["rhm-comms"] });
    };

    const saveRule = useMutation({
        mutationFn: async (p) => {
            const body = { ...p, offset_days: Number(p.offset_days || 0), template_id: p.template_id || null };
            return p.id ? (await rhm.patch(`/automation/rules/${p.id}`, body)).data : (await rhm.post("/automation/rules", body)).data;
        },
        onSuccess: () => { toast.success("Rule saved"); setRule(null); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });
    const saveTpl = useMutation({
        mutationFn: async (p) => p.id
            ? (await rhm.patch(`/automation/templates/${p.id}`, p)).data
            : (await rhm.post("/automation/templates", p)).data,
        onSuccess: () => { toast.success("Template saved"); setTpl(null); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });
    const testRule = useMutation({
        mutationFn: async (id) => (await rhm.post(`/automation/rules/${id}/test`, { to: "test@rehealthme.com" })).data,
        onSuccess: () => { toast.success("Test message queued"); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });
    const scan = useMutation({
        mutationFn: async () => (await rhm.post("/automation/scan")).data,
        onSuccess: (d) => { toast.success(`${d.count} automation action(s) fired`); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });

    const triggerOptions = Object.entries(meta?.triggers || {}).map(([value, label]) => ({ value, label }));
    const actionOptions = Object.entries(meta?.actions || {}).map(([value, label]) => ({ value, label }));

    return (
        <div data-testid="rhm-automation-page">
            <PageHead title="Automation" sub="WHEN something happens → THEN send or create something."
                action={
                    <div className="flex gap-3">
                        {can("automation.manage") && <Btn variant="ghost" onClick={() => scan.mutate()} data-testid="rhm-run-scan"><Zap className="w-3.5 h-3.5 inline mr-1.5" /> RUN SCAN</Btn>}
                        {can("communications.create") && <Btn variant="ghost" onClick={() => setTpl(EMPTY_TPL)} data-testid="rhm-add-template">+ TEMPLATE</Btn>}
                        {can("automation.create") && <Btn onClick={() => setRule(EMPTY_RULE)} data-testid="rhm-add-rule"><Plus className="w-3.5 h-3.5 inline mr-1.5" /> NEW RULE</Btn>}
                    </div>
                } />

            <div className="grid md:grid-cols-3 gap-4 mb-5">
                {Object.entries(comms?.providers || {}).map(([key, p]) => (
                    <div key={key} className="bg-bg1 border border-white/10 p-5">
                        <div className="mono text-[10px] tracking-[0.18em] text-zinc-500">{key.toUpperCase()} CHANNEL</div>
                        <div className="font-display text-xl mt-2">{p.name}</div>
                        <div className="mt-2"><Badge tone={p.connected ? "good" : "warn"}>{p.connected ? "CONNECTED" : "AWAITING CREDENTIALS"}</Badge></div>
                        <p className="text-xs text-zinc-500 mt-2">{p.note}</p>
                    </div>
                ))}
            </div>

            <Card title={`Rules (${rules.length})`} className="mb-5">
                <Table testId="rhm-rules-table" head={["Rule", "When", "Then", "Channel", "Template", "Runs", "Active", ""]}
                    empty={!rules.length && <Empty msg="No automation rules yet." />}>
                    {rules.map((r) => (
                        <tr key={r.id} data-testid={`rhm-rule-row-${r.id}`}>
                            <Td className="font-semibold">{r.name}</Td>
                            <Td className="text-sm text-zinc-400">{r.trigger_label}</Td>
                            <Td className="text-sm text-zinc-400">{r.action_label}</Td>
                            <Td><Badge>{String(r.channel).toUpperCase()}</Badge></Td>
                            <Td className="text-sm text-zinc-400">{r.template_name || "—"}</Td>
                            <Td>{r.run_count || 0}</Td>
                            <Td><Badge tone={r.is_active ? "good" : "muted"}>{r.is_active ? "ON" : "OFF"}</Badge></Td>
                            <Td>
                                <div className="flex gap-3">
                                    {can("automation.edit") && <button onClick={() => setRule(r)} className="mono text-[10px] tracking-[0.16em] text-gold" data-testid={`rhm-edit-rule-${r.id}`}>EDIT</button>}
                                    {can("automation.manage") && <button onClick={() => testRule.mutate(r.id)} className="mono text-[10px] tracking-[0.16em] text-lime" data-testid={`rhm-test-rule-${r.id}`}>TEST</button>}
                                </div>
                            </Td>
                        </tr>
                    ))}
                </Table>
            </Card>

            <Card title={`Templates (${templates.length})`} className="mb-5">
                <Table testId="rhm-templates-table" head={["Name", "Channel", "Audience", "Subject", ""]}
                    empty={!templates.length && <Empty msg="No templates yet." />}>
                    {templates.map((t) => (
                        <tr key={t.id}>
                            <Td className="font-semibold">{t.name}</Td>
                            <Td><Badge>{String(t.channel).toUpperCase()}</Badge></Td>
                            <Td className="text-sm text-zinc-400">{titleCase(t.audience || "")}</Td>
                            <Td className="text-sm text-zinc-400 max-w-[280px] truncate">{t.subject}</Td>
                            <Td>{can("communications.edit") && <button onClick={() => setTpl(t)} className="mono text-[10px] tracking-[0.16em] text-gold" data-testid={`rhm-edit-template-${t.id}`}>EDIT</button>}</Td>
                        </tr>
                    ))}
                </Table>
            </Card>

            <Card title={`Communication log (${comms?.messages?.length || 0})`}>
                <Table testId="rhm-comms-table" head={["When", "Channel", "To", "Subject", "Rule", "Status"]}
                    empty={!comms?.messages?.length && <Empty msg="Nothing queued yet." />}>
                    {(comms?.messages || []).map((m) => (
                        <tr key={m.id} data-testid={`rhm-comm-row-${m.id}`}>
                            <Td className="text-xs text-zinc-500 whitespace-nowrap">{dt(m.created_at)}</Td>
                            <Td><Badge>{String(m.channel).toUpperCase()}</Badge></Td>
                            <Td className="text-sm">{m.to}</Td>
                            <Td className="text-sm text-zinc-300 max-w-[280px] truncate">{m.subject}</Td>
                            <Td className="text-sm text-zinc-500">{m.rule_name || "manual"}</Td>
                            <Td><Badge tone={m.status === "sent" ? "good" : "warn"}>{String(m.status).toUpperCase()}</Badge></Td>
                        </tr>
                    ))}
                </Table>
            </Card>

            <Modal open={!!rule} onClose={() => setRule(null)} wide testId="rhm-rule-modal" title={rule?.id ? "Edit Rule" : "New Automation Rule"}>
                {rule && (
                    <form onSubmit={(e) => { e.preventDefault(); saveRule.mutate(rule); }} className="space-y-5">
                        <Field label="Rule name" required><Input required value={rule.name} onChange={(e) => setRule({ ...rule, name: e.target.value })} data-testid="rule-name" /></Field>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="border border-gold/30 bg-gold/5 p-4">
                                <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">WHEN</div>
                                <Select value={rule.trigger} onChange={(e) => setRule({ ...rule, trigger: e.target.value })} options={triggerOptions} data-testid="rule-trigger" />
                                <div className="mt-3">
                                    <Field label="Offset / threshold (days)" hint="Used by expiry and reminder timing">
                                        <Input type="number" value={rule.offset_days} onChange={(e) => setRule({ ...rule, offset_days: e.target.value })} />
                                    </Field>
                                </div>
                            </div>
                            <div className="border border-white/12 p-4">
                                <div className="mono text-[10px] tracking-[0.2em] text-lime mb-3">THEN</div>
                                <Select value={rule.action} onChange={(e) => setRule({ ...rule, action: e.target.value })} options={actionOptions} data-testid="rule-action" />
                                <div className="grid grid-cols-2 gap-3 mt-3">
                                    <Field label="Channel"><Select value={rule.channel} onChange={(e) => setRule({ ...rule, channel: e.target.value })} options={meta?.channels || ["email"]} data-testid="rule-channel" /></Field>
                                    <Field label="Audience"><Select value={rule.audience} onChange={(e) => setRule({ ...rule, audience: e.target.value })} options={meta?.audiences || ["customer"]} /></Field>
                                </div>
                                <div className="mt-3">
                                    <Field label="Template">
                                        <Select value={rule.template_id || ""} onChange={(e) => setRule({ ...rule, template_id: e.target.value })} data-testid="rule-template">
                                            <option value="">No template (uses rule name)</option>
                                            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                        </Select>
                                    </Field>
                                </div>
                            </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label="Active"><Select value={rule.is_active ? "yes" : "no"} onChange={(e) => setRule({ ...rule, is_active: e.target.value === "yes" })}
                                options={[{ value: "yes", label: "On" }, { value: "no", label: "Off" }]} data-testid="rule-active" /></Field>
                            <Field label="Description"><Input value={rule.description} onChange={(e) => setRule({ ...rule, description: e.target.value })} /></Field>
                        </div>
                        <Btn type="submit" disabled={saveRule.isPending} data-testid="rule-submit">SAVE RULE</Btn>
                    </form>
                )}
            </Modal>

            <Modal open={!!tpl} onClose={() => setTpl(null)} wide testId="rhm-template-modal" title={tpl?.id ? "Edit Template" : "New Template"}>
                {tpl && (
                    <form onSubmit={(e) => { e.preventDefault(); saveTpl.mutate(tpl); }} className="space-y-4">
                        <div className="grid md:grid-cols-3 gap-4">
                            <Field label="Name" required><Input required value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} data-testid="template-name" /></Field>
                            <Field label="Channel"><Select value={tpl.channel} onChange={(e) => setTpl({ ...tpl, channel: e.target.value })} options={meta?.channels || ["email"]} data-testid="template-channel" /></Field>
                            <Field label="Audience"><Select value={tpl.audience} onChange={(e) => setTpl({ ...tpl, audience: e.target.value })} options={meta?.audiences || ["customer"]} /></Field>
                        </div>
                        <Field label="Subject"><Input value={tpl.subject} onChange={(e) => setTpl({ ...tpl, subject: e.target.value })} data-testid="template-subject" /></Field>
                        <Field label="Body" hint={`Variables: ${(meta?.variables || []).map((v) => `{{${v}}}`).join(" ")}`}>
                            <Textarea rows={7} value={tpl.body} onChange={(e) => setTpl({ ...tpl, body: e.target.value })} data-testid="template-body" />
                        </Field>
                        <div className="border border-white/10 p-4">
                            <div className="mono text-[10px] tracking-[0.18em] text-gold mb-2">PREVIEW</div>
                            <div className="text-sm font-semibold">{tpl.subject.replace(/\{\{customer_name\}\}/g, "Ravi").replace(/\{\{product_name\}\}/g, "ReDrive Max")}</div>
                            <div className="text-sm text-zinc-400 whitespace-pre-wrap mt-2">
                                {tpl.body.replace(/\{\{customer_name\}\}/g, "Ravi").replace(/\{\{product_name\}\}/g, "ReDrive Max").replace(/\{\{order_number\}\}/g, "RHM2600001")}
                            </div>
                        </div>
                        <Btn type="submit" disabled={saveTpl.isPending} data-testid="template-submit">SAVE TEMPLATE</Btn>
                    </form>
                )}
            </Modal>
        </div>
    );
}
